import { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: ReactNode;
	size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
	showCloseButton?: boolean;
	/** Custom header content - when provided, replaces default header */
	headerContent?: ReactNode;
	/** Whether to show the default header with border */
	showHeader?: boolean;
	/** Additional class for the modal panel */
	panelClassName?: string;
	/** Additional class for the body/content area */
	bodyClassName?: string;
}

const Modal = ({
	isOpen,
	onClose,
	title,
	children,
	size = 'md',
	showCloseButton = true,
	headerContent,
	showHeader = true,
	panelClassName = '',
	bodyClassName = '',
}: ModalProps) => {
	const sizeClasses = {
		sm: 'max-w-md',
		md: 'max-w-lg',
		lg: 'max-w-2xl',
		xl: 'max-w-4xl',
		'2xl': 'max-w-6xl',
	};

	const hasHeader = showHeader && (title || headerContent || showCloseButton);

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				{/* Backdrop */}
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
				</Transition.Child>

				{/* Modal container - scrollable on mobile */}
				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 sm:p-6">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95 translate-y-4"
							enterTo="opacity-100 scale-100 translate-y-0"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100 translate-y-0"
							leaveTo="opacity-0 scale-95 translate-y-4"
						>
							<Dialog.Panel
								className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left align-middle shadow-2xl transition-all ${panelClassName}`}
							>
								{/* Custom Header */}
								{headerContent && (
									<div className="relative">
										{headerContent}
										{showCloseButton && (
											<button
												type="button"
												className="absolute top-1/2 right-4 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 focus:outline-none transition-all cursor-pointer"
												onClick={onClose}
											>
												<span className="sr-only">Close</span>
												<XMarkIcon className="h-5 w-5" />
											</button>
										)}
									</div>
								)}

								{/* Default Header */}
								{!headerContent && hasHeader && (
									<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
										{title && (
											<Dialog.Title
												as="h3"
												className="text-lg font-semibold text-gray-900 dark:text-gray-100"
											>
												{title}
											</Dialog.Title>
										)}
										{showCloseButton && (
											<button
												type="button"
												className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none ml-auto transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
												onClick={onClose}
											>
												<span className="sr-only">Close</span>
												<XMarkIcon className="h-6 w-6" />
											</button>
										)}
									</div>
								)}

								{/* Body */}
								<div className={`px-6 py-5 ${bodyClassName}`}>{children}</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default Modal;
